import React from 'react';
import { motion } from 'framer-motion';
import { HelpCircle, Sparkles, BookOpen } from 'lucide-react';

const FAQHeroBit = ({ isMobile = false }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-pearl via-pearl to-nude/30 p-6 md:p-12 text-center"
        >
            {/* Decorative Pattern */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239CAF96' fill-opacity='0.1'%3E%3Cpath d='M50 50c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10s-10-4.477-10-10 4.477-10 10-10zM10 10c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10S0 25.523 0 20s4.477-10 10-10zm10 8c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8zm40 40c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    backgroundSize: '80px 80px'
                }} />
            </div>

            <div className="relative z-10">
                {/* Icons */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1 }}
                    className="flex justify-center gap-4 mb-6"
                >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-champagne/20 to-sage/20 flex items-center justify-center">
                        <HelpCircle className="w-6 h-6 text-champagne" />
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sage/20 to-champagne/20 flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-sage" />
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-champagne/20 to-sage/20 flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-champagne" />
                    </div>
                </motion.div>

                {/* Title */}
                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.8 }}
                    className={`font-heading font-light text-charcoal mb-4 ${
                        isMobile ? 'text-3xl' : 'text-4xl md:text-5xl'
                    }`}
                >
                    Your KROMAIC questions,
                    <br />answered
                </motion.h1>

                {/* Subtitle */}
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.8 }}
                    className={`text-charcoal/60 max-w-2xl mx-auto ${
                        isMobile ? 'text-base' : 'text-lg md:text-xl'
                    }`}
                >
                    Find guidance on orders, shipping, product usage, and everything
                    you need to enhance your beauty ritual.
                </motion.p>

                {/* Stats */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-4'} gap-4 mt-8 max-w-md mx-auto`}
                >
                    {[
                        { value: '100+', label: 'Questions' },
                        { value: '24/7', label: 'Updated' },
                        { value: '95%', label: 'Answered' },
                        { value: 'Instant', label: 'Access' }
                    ].map((stat, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.4 + index * 0.1 }}
                            className="text-center p-3 rounded-xl bg-white/50 backdrop-blur-sm"
                        >
                            <div className="text-lg md:text-xl font-light text-charcoal mb-1">{stat.value}</div>
                            <div className="text-xs text-charcoal/50">{stat.label}</div>
                        </motion.div>
                    ))}
                </motion.div>
            </div>

            {/* Floating Elements */}
            <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                className={`absolute ${isMobile ? 'right-4 top-4 w-12 h-12' : 'right-8 top-8 w-16 h-16'} opacity-5`}
            >
                <svg viewBox="0 0 100 100" className="w-full h-full text-sage">
                    <circle cx="50" cy="50" r="45" stroke="currentColor" strokeWidth="1" fill="none" />
                </svg>
            </motion.div>
        </motion.div>
    );
};

export default FAQHeroBit;